import Decimal from "decimal.js-light";

const HUNDREDTH = new Decimal("0.01");

type StopUnitDimensions = {
  quantity: Decimal | null;
  width: Decimal | null;
  length: Decimal | null;
};

// Unrounded loadmeters taken up by a stop unit, 0 until its quantity, width
// and length are all filled in.
export const stopUnitLoadmeters = (su: StopUnitDimensions): Decimal =>
  su.quantity && su.width && su.length
    ? new Decimal(String(su.width))
        .times(String(su.length))
        .times(String(su.quantity))
        .div(24000)
    : new Decimal(0);

// Rounds each value to 2 decimals so that together they add up to `total`
// (largest remainder method): every value is rounded down, then the
// hundredths still missing go one each to the values that lost the most,
// ties to the earlier one. Returns null when `total` can't be reached that
// way, i.e. when some value would have to move by a hundredth or more.
export const roundLoadmetersToTotal = (
  values: Decimal[],
  total: Decimal
): Decimal[] | null => {
  const floored = values.map((v) => v.toDecimalPlaces(2, Decimal.ROUND_DOWN));
  const missing = total
    .minus(floored.reduce((acc, v) => acc.add(v), new Decimal(0)))
    .div(HUNDREDTH);
  if (!missing.isInteger() || missing.lt(0) || missing.gt(values.length)) {
    return null;
  }
  const byRemainder = values
    .map((v, i) => ({ i, remainder: v.minus(floored[i]) }))
    .sort((a, b) => b.remainder.comparedTo(a.remainder) || a.i - b.i);
  const result = [...floored];
  for (const { i } of byRemainder.slice(0, missing.toNumber())) {
    result[i] = result[i].add(HUNDREDTH);
  }
  return result;
};

// Rounds each value to 2 decimals so that they still add up to their rounded
// total. Rounding each on its own doesn't: two units of 1/3 lm picked up
// together round to 0.67, but dropped off at two stops round to
// 0.33 + 0.33 = 0.66, and the server rejects the cargo because pickup and
// dropoff totals must be equal.
export const roundLoadmetersPreservingTotal = (values: Decimal[]): Decimal[] =>
  roundLoadmetersToTotal(
    values,
    values
      .reduce((acc, v) => acc.add(v), new Decimal(0))
      .toDecimalPlaces(2, Decimal.ROUND_HALF_UP)
  )!;
